import type { AxiosPromise } from 'axios';
import BaseClient, { type ExtendedAxiosRequestConfig } from './BaseClient';
import type { CursorPaginationResponse } from './api';
import type { CursorPaginationParams } from './BaseClient.types';
import type {
    PendingAcceptanceGroup,
    PendingGroupsParams,
    Term,
    TermAcceptance,
    TermListParams,
    TermPayload,
    TermStatus,
    TermStatusLog,
} from './adminTermsClient.types';

class AdminTermsClient extends BaseClient {
    getTerms(teamId: number, params?: TermListParams): AxiosPromise<CursorPaginationResponse<Term>> {
        const url = `/api/admin/teams/${teamId}/terms`;
        return this.makeCoreApiRequest(url, 'get', null, params);
    }

    getTerm(teamId: number, termId: number): AxiosPromise<Term> {
        const url = `/api/admin/teams/${teamId}/terms/${termId}`;
        return this.makeCoreApiRequest(url);
    }

    createTerm(teamId: number, data: TermPayload): AxiosPromise<Term> {
        const url = `/api/admin/teams/${teamId}/terms`;
        return this.makeCoreApiRequest(url, 'post', data);
    }

    updateTerm(teamId: number, termId: number, data: TermPayload): AxiosPromise<Term> {
        const url = `/api/admin/teams/${teamId}/terms/${termId}`;
        return this.makeCoreApiRequest(url, 'put', data);
    }

    updateTermStatus(teamId: number, termId: number, status: Exclude<TermStatus, 'draft'>): AxiosPromise<Term> {
        const url = `/api/admin/teams/${teamId}/terms/${termId}/status`;
        return this.makeCoreApiRequest(url, 'patch', { status });
    }

    deleteTerm(teamId: number, termId: number): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/terms/${termId}`;
        return this.makeCoreApiRequest(url, 'delete');
    }

    getTermAcceptances(
        teamId: number,
        termId: number,
        params?: CursorPaginationParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<CursorPaginationResponse<TermAcceptance>> {
        const url = `/api/admin/teams/${teamId}/terms/${termId}/acceptances`;
        return this.makeCoreApiRequest(url, 'get', null, params, config);
    }

    getPendingAcceptanceGroups(
        teamId: number,
        termId: number,
        params?: PendingGroupsParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<CursorPaginationResponse<PendingAcceptanceGroup>> {
        const url = `/api/admin/teams/${teamId}/terms/${termId}/acceptances/pending`;
        return this.makeCoreApiRequest(url, 'get', null, params, config);
    }

    getTermStatusLogs(
        teamId: number,
        termId: number,
        params?: CursorPaginationParams
    ): AxiosPromise<CursorPaginationResponse<TermStatusLog>> {
        const url = `/api/admin/teams/${teamId}/terms/${termId}/status-logs`;
        return this.makeCoreApiRequest(url, 'get', null, params);
    }
}

export default new AdminTermsClient();
